import type { HeightmapAsset } from '../types';
import { clamp01 } from '../math/scalar';
import { createId } from '../design/defaults';

/**
 * Image import — the "drop your own image" path.
 *
 * A photograph makes a poor relief straight out of the camera: sensor noise
 * becomes 0.1 mm spikes that a nozzle cannot print, and the useful contrast
 * usually sits in a narrow band. So the import pipeline is deliberately
 * opinionated — downscale, convert to luminance, blur, then stretch the levels.
 *
 * `heightmapFromPixels` is pure and takes raw RGBA, which keeps the decisions
 * testable without a DOM.
 */

export interface ImageImportOptions {
  /** Longest edge after downsampling. Bigger costs memory, not detail. */
  maxSize?: number;
  /** Smoothing strength, `0 .. 1`. Suppresses noise that would print as spikes. */
  smoothing?: number;
  /** Stretches the levels to the full range. */
  normalize?: boolean;
  invert?: boolean;
  name?: string;
}

export const DEFAULT_IMPORT_OPTIONS: Required<Omit<ImageImportOptions, 'name'>> = {
  maxSize: 1024,
  smoothing: 0.3,
  normalize: true,
  invert: false,
};

/** Relative luminance, the perceptual brightness a viewer reads as "height". */
function luminance(r: number, g: number, b: number): number {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

/** Separable box blur, run three times to approximate a Gaussian. */
function blur(data: Float32Array, width: number, height: number, radius: number): Float32Array {
  if (radius < 1) return data;

  // Explicitly widened: the two buffers are swapped every pass, so both names
  // have to accept either backing store.
  let source: Float32Array = data;
  let target: Float32Array = new Float32Array(data.length);

  for (let pass = 0; pass < 3; pass += 1) {
    // Horizontal
    for (let y = 0; y < height; y += 1) {
      const row = y * width;
      for (let x = 0; x < width; x += 1) {
        let sum = 0;
        let count = 0;
        for (let k = -radius; k <= radius; k += 1) {
          const sx = x + k;
          if (sx < 0 || sx >= width) continue;
          sum += source[row + sx];
          count += 1;
        }
        target[row + x] = sum / count;
      }
    }
    let swap = source;
    source = target;
    target = swap;

    // Vertical
    for (let x = 0; x < width; x += 1) {
      for (let y = 0; y < height; y += 1) {
        let sum = 0;
        let count = 0;
        for (let k = -radius; k <= radius; k += 1) {
          const sy = y + k;
          if (sy < 0 || sy >= height) continue;
          sum += source[sy * width + x];
          count += 1;
        }
        target[y * width + x] = sum / count;
      }
    }
    swap = source;
    source = target;
    target = swap;
  }

  return source;
}

/** Stretches the values so the darkest sample is 0 and the brightest is 1. */
function normalizeRange(data: Float32Array): void {
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < data.length; i += 1) {
    if (data[i] < min) min = data[i];
    if (data[i] > max) max = data[i];
  }
  const span = max - min;
  if (span < 1e-5) return;
  for (let i = 0; i < data.length; i += 1) {
    data[i] = (data[i] - min) / span;
  }
}

/** Box-filtered downsample. Averaging beats point sampling for heightmaps. */
function downsample(
  data: Float32Array,
  width: number,
  height: number,
  targetWidth: number,
  targetHeight: number,
): Float32Array {
  const result = new Float32Array(targetWidth * targetHeight);
  const scaleX = width / targetWidth;
  const scaleY = height / targetHeight;

  for (let y = 0; y < targetHeight; y += 1) {
    const y0 = Math.floor(y * scaleY);
    const y1 = Math.max(y0 + 1, Math.floor((y + 1) * scaleY));
    for (let x = 0; x < targetWidth; x += 1) {
      const x0 = Math.floor(x * scaleX);
      const x1 = Math.max(x0 + 1, Math.floor((x + 1) * scaleX));

      let sum = 0;
      let count = 0;
      for (let sy = y0; sy < Math.min(y1, height); sy += 1) {
        for (let sx = x0; sx < Math.min(x1, width); sx += 1) {
          sum += data[sy * width + sx];
          count += 1;
        }
      }
      result[y * targetWidth + x] = count > 0 ? sum / count : 0;
    }
  }

  return result;
}

/** Converts raw RGBA pixels into a height map asset. */
export function heightmapFromPixels(
  pixels: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  options: ImageImportOptions = {},
): HeightmapAsset {
  const settings = { ...DEFAULT_IMPORT_OPTIONS, ...options };

  if (width <= 0 || height <= 0 || pixels.length < width * height * 4) {
    throw new Error('Image data does not match the given dimensions.');
  }

  let data: Float32Array = new Float32Array(width * height);
  for (let i = 0; i < data.length; i += 1) {
    const o = i * 4;
    const alpha = pixels[o + 3] / 255;
    // Transparent areas read as "no relief" rather than as black.
    data[i] = luminance(pixels[o], pixels[o + 1], pixels[o + 2]) * alpha;
  }

  let currentWidth = width;
  let currentHeight = height;
  const longest = Math.max(width, height);
  if (longest > settings.maxSize) {
    const scale = settings.maxSize / longest;
    currentWidth = Math.max(1, Math.round(width * scale));
    currentHeight = Math.max(1, Math.round(height * scale));
    data = downsample(data, width, height, currentWidth, currentHeight);
  }

  if (settings.smoothing > 0) {
    const radius = Math.round(settings.smoothing * Math.max(currentWidth, currentHeight) * 0.01);
    data = blur(data, currentWidth, currentHeight, radius);
  }

  if (settings.normalize) normalizeRange(data);

  if (settings.invert) {
    for (let i = 0; i < data.length; i += 1) data[i] = 1 - data[i];
  }

  for (let i = 0; i < data.length; i += 1) data[i] = clamp01(data[i]);

  return {
    id: createId('asset'),
    name: options.name ?? 'Imported image',
    width: currentWidth,
    height: currentHeight,
    data,
  };
}

/** Decodes a browser `Blob` and imports it. Browser only. */
export async function heightmapFromBlob(
  blob: Blob,
  options: ImageImportOptions = {},
): Promise<HeightmapAsset> {
  const bitmap = await createImageBitmap(blob);
  try {
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;

    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) throw new Error('Could not get a 2D drawing context.');

    context.drawImage(bitmap, 0, 0);
    const imageData = context.getImageData(0, 0, bitmap.width, bitmap.height);

    return heightmapFromPixels(imageData.data, bitmap.width, bitmap.height, {
      name: blob instanceof File ? blob.name.replace(/\.[^.]+$/, '') : 'Imported image',
      ...options,
    });
  } finally {
    bitmap.close();
  }
}

export const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg,image/webp,image/gif,image/bmp';
